<section class="sav-events-page text-[var(--color-neutral-900)]">
  <!-- No horizontal padding here: every child either breaks out full-bleed and applies the site
       gutter itself, or is a centred max-width block. A `px-6` here shrank the containing block, so
       the children's `%` gutters resolved ~4px narrower than the rest of the site. -->
  <div class="py-[50px]">
    <div class="sav-site-container">
      <app-breadcrumb [items]="crumbs" />
    </div>

    <section
      class="sav-events-calendar-intro sav-reveal"
      aria-label="Sales and shopping events calendar"
    >
      <!-- No year in the copy. The calendar is a rolling one — the events run from this month into
           next year — so a fixed year here was both wrong for half the listings and something that
           would need editing every January. The dates on the cards say when each sale is. -->
      <span
        class="sav-events-kicker inline-block px-[12px] py-[4px] mb-[10px] text-[10px] font-bold tracking-widest uppercase rounded-full"
        >Global Sales Tracker</span
      >
      <h1>Sales &amp; Shopping Events Calendar &#64;SavingArena</h1>
      <p>
        Plan smarter with our sales and shopping events calendar. Discover major online sale dates,
        track countdowns to upcoming deals, and use verified coupon codes from top stores before
        offers expire.
      </p>
    </section>

    @if (activeEvents().length > 0) {
      <section class="sav-reveal mb-[50px]">
        <div class="sav-events-live-stack">
          @for (event of activeEvents(); track event.id) {
            <div
              class="group relative bg-white border border-[var(--color-neutral-100)] rounded-[18px] sav-premium-shadow overflow-hidden p-2"
            >
              <div class="flex flex-col lg:flex-row min-h-[500px]">
                <div
                  class="sav-events-black-friday-panel lg:w-2/5 relative rounded-[2rem] overflow-hidden flex items-center justify-center p-12"
                  [style.background-image]="event.thumbnailBackgroundImage"
                >
                  <div class="sav-events-black-friday-content relative z-10">
                    <h2 class="sav-events-black-friday-title text-white">
                      {{ event.uppercaseName }}
                    </h2>
                    <div class="sav-events-live-pill inline-flex gap-2">
                      <span>Live Now</span>
                    </div>
                  </div>
                </div>
                <div
                  class="lg:w-3/5 p-[20px_calc(var(--spacing)_*_12)] flex flex-col justify-center"
                >
                  <h3 class="text-[var(--color-primary)] mb-4">{{ event.name }} Premium Access</h3>
                  <p class="text-[14px] text-[var(--color-neutral-700)] leading-relaxed mb-[50px]">
                    Explore limited-time online shopping deals for {{ event.name }}, track verified
                    coupon drops from top stores, and plan each purchase with confidence before this
                    sale event expires.
                  </p>
                  <div class="flex flex-col sm:flex-row gap-4 mb-[20px]">
                    <a class="sav-events-cta sav-events-cta-primary" [routerLink]="event.routeLink">
                      <span>View Event Deals</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M16.0037 9.41421L7.39712 18.0208L5.98291 16.6066L14.5895 8H7.00373V6H18.0037V17H16.0037V9.41421Z"
                        ></path>
                      </svg>
                    </a>
                    <a class="sav-events-cta sav-events-cta-ghost" [routerLink]="event.routeLink">
                      <span>View All Coupons</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M16.0037 9.41421L7.39712 18.0208L5.98291 16.6066L14.5895 8H7.00373V6H18.0037V17H16.0037V9.41421Z"
                        ></path>
                      </svg>
                    </a>
                  </div>
                  @if (countdownParts(event.endTimeMs); as countdown) {
                    <div class="grid grid-cols-4 gap-4 mt-auto">
                      <div class="sav-events-count-card p-4 rounded-[14px] text-center">
                        <span class="sav-events-count-value block text-2xl font-bold">{{
                          countdown.days
                        }}</span
                        ><span class="sav-events-count-label text-[10px] uppercase font-bold"
                          >Days</span
                        >
                      </div>
                      <div class="sav-events-count-card p-4 rounded-[14px] text-center">
                        <span class="sav-events-count-value block text-2xl font-bold">{{
                          countdown.hours
                        }}</span
                        ><span class="sav-events-count-label text-[10px] uppercase font-bold"
                          >Hours</span
                        >
                      </div>
                      <div class="sav-events-count-card p-4 rounded-[14px] text-center">
                        <span class="sav-events-count-value block text-2xl font-bold">{{
                          countdown.mins
                        }}</span
                        ><span class="sav-events-count-label text-[10px] uppercase font-bold"
                          >Mins</span
                        >
                      </div>
                      <div class="sav-events-count-card p-4 rounded-[14px] text-center">
                        <span class="sav-events-count-value block text-2xl font-bold">{{
                          countdown.secs
                        }}</span
                        ><span class="sav-events-count-label text-[10px] uppercase font-bold"
                          >Secs</span
                        >
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      </section>
    }

    <section class="sav-events-featured-block sav-reveal" aria-label="Featured upcoming events">
      <div class="sav-events-section-head">
        <h2>Featured Upcoming Events</h2>
        <p>
          These are the next major sale events worth preparing for. Save the dates, see what's
          coming up, and focus on the events that deliver the biggest savings.
        </p>
      </div>

      <div class="sav-events-featured-grid">
        @if (isLoadingEvents()) {
          <div class="sav-events-empty-state">Loading upcoming events...</div>
        } @else if (featuredUpcomingEvents().length > 0) {
          @for (event of featuredUpcomingEvents(); track event.id) {
            <a class="sav-events-featured-card" [routerLink]="event.routeLink">
              <div
                class="sav-events-featured-media sav-events-featured-media-api"
                [style.background-image]="event.thumbnailBackgroundImage"
              >
                <span>{{ daysUntilLabel(event.startTimeMs) }}</span>
              </div>
              <div class="sav-events-featured-content">
                <h3>{{ event.uppercaseName }}</h3>
                <p>{{ event.dateLabel }}</p>
              </div>
            </a>
          }
        } @else {
          <div class="sav-events-empty-state">No upcoming events are available right now.</div>
        }
      </div>
    </section>

    <section
      class="sav-events-upcoming-block sav-reveal border-t border-[var(--color-neutral-100)]"
    >
      <div>
        <div class="flex flex-col md:flex-row justify-between items-end mb-12">
          <div class="sav-events-section-head">
            <h2 class="text-[var(--color-neutral-900)]">More Upcoming Events</h2>
            <p class="text-[var(--color-neutral-700)] text-[16px] mt-2">
              Discover upcoming sale windows not included in live, featured, or past event lists.
            </p>
          </div>
        </div>
      </div>

      <!-- `sav-events-list-grid` carries the row-completion rules; the column counts here and the
           nth-child steps in the stylesheet have to stay in step. -->
      <div
        class="sav-events-list-grid grid grid-cols-1 gap-6 min-[561px]:grid-cols-2 min-[1101px]:grid-cols-3 min-[1501px]:grid-cols-4"
      >
        @if (isLoadingEvents()) {
          <div class="sav-events-empty-state sav-events-empty-state-wide">
            Loading upcoming events...
          </div>
        } @else if (moreUpcomingEvents().length > 0) {
          @for (event of moreUpcomingEvents(); track event.id) {
            <a
              class="sav-archive-card bg-white border border-[var(--color-neutral-100)] p-6 rounded-[14px] sav-premium-shadow"
              [routerLink]="event.routeLink"
            >
              <div class="flex justify-between items-start mb-6">
                <div
                  class="sav-events-api-icon w-10 h-10 bg-[var(--color-primary-tint)] rounded-[12px] flex items-center justify-center"
                >
                  @if (event.iconHtml) {
                    <span [innerHTML]="event.iconHtml"></span>
                  } @else if (event.iconImageUrl) {
                    <img [src]="event.iconImageUrl" [alt]="event.name" />
                  } @else {
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7" />
                      <path d="M2 7h20v5H2z" />
                      <path d="M12 22V7" />
                    </svg>
                  }
                </div>
                <span
                  class="text-[10px] font-bold bg-[var(--color-primary-tint)] px-2 py-1 rounded text-[var(--color-neutral-700)]"
                  >{{ event.monthLabel }}</span
                >
              </div>
              <h3>{{ event.name }}</h3>
              <p class="text-xs text-[var(--color-neutral-700)] mt-1">
                {{ eventMeta('Upcoming', event) }}
              </p>
            </a>
          }
        } @else {
          <div class="sav-events-empty-state sav-events-empty-state-wide">
            No additional upcoming events are available right now.
          </div>
        }
      </div>
    </section>

    <section
      class="sav-events-archives-block sav-reveal border-t border-[var(--color-neutral-100)]"
    >
      <div>
        <div class="flex flex-col md:flex-row justify-between items-end mb-12">
          <div class="sav-events-section-head">
            <h2 class="text-[var(--color-neutral-900)]">Historical Archives</h2>
            <p class="text-[var(--color-neutral-700)] text-[16px] mt-2">
              Browse current-year performance and top deals from concluded events.
            </p>
          </div>
        </div>
      </div>

      <!-- `sav-events-list-grid` carries the row-completion rules; the column counts here and the
           nth-child steps in the stylesheet have to stay in step. -->
      <div
        class="sav-events-list-grid grid grid-cols-1 gap-6 min-[561px]:grid-cols-2 min-[1101px]:grid-cols-3 min-[1501px]:grid-cols-4"
      >
        @if (isLoadingEvents()) {
          <div class="sav-events-empty-state sav-events-empty-state-wide">
            Loading historical events...
          </div>
        } @else if (archiveEvents().length > 0) {
          @for (event of archiveEvents(); track event.id) {
            <a
              class="sav-archive-card bg-white border border-[var(--color-neutral-100)] p-6 rounded-[14px] sav-premium-shadow"
              [routerLink]="event.routeLink"
            >
              <div class="flex justify-between items-start mb-6">
                <div
                  class="sav-events-api-icon w-10 h-10 bg-[var(--color-primary-tint)] rounded-[12px] flex items-center justify-center"
                >
                  @if (event.iconHtml) {
                    <span [innerHTML]="event.iconHtml"></span>
                  } @else if (event.iconImageUrl) {
                    <img [src]="event.iconImageUrl" [alt]="event.name" />
                  } @else {
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7" />
                      <path d="M2 7h20v5H2z" />
                      <path d="M12 22V7" />
                    </svg>
                  }
                </div>
                <span
                  class="text-[10px] font-bold bg-[var(--color-primary-tint)] px-2 py-1 rounded text-[var(--color-neutral-700)]"
                  >{{ event.monthLabel }}</span
                >
              </div>
              <h3>{{ event.name }}</h3>
              <p class="text-xs text-[var(--color-neutral-700)] mt-1">
                {{ eventMeta('Concluded', event) }}
              </p>
            </a>
          }
        } @else {
          <div class="sav-events-empty-state sav-events-empty-state-wide">
            No concluded current-year events are available right now.
          </div>
        }
      </div>
    </section>

    <app-latest-insights />

    <section class="sav-faq-section" aria-label="Frequently Asked Questions">
      <div class="sav-faq-wrap">
        <h2 class="sav-events-static-section-title">
          Frequently Asked Questions<app-dev-flag
            note="Hardcoded FAQ list — should be admin-managed"
          />
        </h2>
        <p>What shoppers ask about sale events and how to prepare for them.</p>
        <div class="sav-faq-list">
          <div class="sav-faq-item" [class.expanded]="expandedFaqIndex() === 0">
            <button
              type="button"
              class="sav-faq-question"
              [attr.aria-expanded]="expandedFaqIndex() === 0"
              (click)="toggleFaq(0)"
            >
              <span>How do I use a coupon code on SavingArena?</span>
              <svg
                class="sav-faq-chevron"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div class="sav-faq-answer" [class.visible]="expandedFaqIndex() === 0">
              <p>
                Browse our catalog of coupons and deals, click on the one you want to use, and copy
                the code. Then shop at the retailer's website and paste the code at checkout to
                apply your savings.
              </p>
            </div>
          </div>

          <div class="sav-faq-item" [class.expanded]="expandedFaqIndex() === 1">
            <button
              type="button"
              class="sav-faq-question"
              [attr.aria-expanded]="expandedFaqIndex() === 1"
              (click)="toggleFaq(1)"
            >
              <span>Are the coupons on SavingArena really free?</span>
              <svg
                class="sav-faq-chevron"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div class="sav-faq-answer" [class.visible]="expandedFaqIndex() === 1">
              <p>
                Yes! All coupons and deals on SavingArena are completely free to use. We partner
                with retailers to bring you exclusive discounts without any hidden fees or
                subscription requirements.
              </p>
            </div>
          </div>

          <div class="sav-faq-item" [class.expanded]="expandedFaqIndex() === 2">
            <button
              type="button"
              class="sav-faq-question"
              [attr.aria-expanded]="expandedFaqIndex() === 2"
              (click)="toggleFaq(2)"
            >
              <span>How often are the deals and coupons updated?</span>
              <svg
                class="sav-faq-chevron"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div class="sav-faq-answer" [class.visible]="expandedFaqIndex() === 2">
              <p>
                Our team refreshes deals daily to ensure you have access to the latest discounts. We
                remove expired codes automatically and add new ones as soon as they become available
                from our retail partners.
              </p>
            </div>
          </div>

          <div class="sav-faq-item" [class.expanded]="expandedFaqIndex() === 3">
            <button
              type="button"
              class="sav-faq-question"
              [attr.aria-expanded]="expandedFaqIndex() === 3"
              (click)="toggleFaq(3)"
            >
              <span>Do I need to create an account to use SavingArena?</span>
              <svg
                class="sav-faq-chevron"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div class="sav-faq-answer" [class.visible]="expandedFaqIndex() === 3">
              <p>
                No account is required. You can browse and copy coupon codes immediately. However,
                creating a free account gives you access to personalized deal alerts, price drop
                notifications, and a saved wishlist.
              </p>
            </div>
          </div>

          <div class="sav-faq-item" [class.expanded]="expandedFaqIndex() === 4">
            <button
              type="button"
              class="sav-faq-question"
              [attr.aria-expanded]="expandedFaqIndex() === 4"
              (click)="toggleFaq(4)"
            >
              <span>How does SavingArena make money?</span>
              <svg
                class="sav-faq-chevron"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div class="sav-faq-answer" [class.visible]="expandedFaqIndex() === 4">
              <p>
                We earn a small commission from retailers when you make a purchase using our links
                or codes. This doesn't affect the savings you get - in fact, it allows us to keep
                the service free for everyone.
              </p>
            </div>
          </div>

          <div class="sav-faq-item" [class.expanded]="expandedFaqIndex() === 5">
            <button
              type="button"
              class="sav-faq-question"
              [attr.aria-expanded]="expandedFaqIndex() === 5"
              (click)="toggleFaq(5)"
            >
              <span>What should I do if a coupon code doesn't work?</span>
              <svg
                class="sav-faq-chevron"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div class="sav-faq-answer" [class.visible]="expandedFaqIndex() === 5">
              <p>
                If a code doesn't work, first double-check that you've entered it correctly. Some
                codes have minimum purchase requirements or exclusions. If it still fails, please
                report it so we can verify or replace it promptly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</section>

